import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { supabase, mirrorToken } from "../lib/supabase";

const AuthContext = createContext(null);

// Supabase Auth owns identity/session (restore on refresh, token refresh, multi-tab sign-out). The backend maps the
// Supabase user to the existing public.users row; legacy HS256 tokens still work during the migration window.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [balance, setBalance] = useState(10000);
  const [layer, setLayer] = useState(null); // "supabase" | "legacy"

  const loadMe = useCallback(async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data.user); setBalance(data.user.balance ?? 10000); return true; }
    catch { return false; }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      let data = { session: null };
      try { ({ data } = await supabase.auth.getSession()); } catch {}
      if (!alive) return;
      if (data.session) { mirrorToken(data.session); setLayer("supabase"); if (!(await loadMe())) { await supabase.auth.signOut(); localStorage.removeItem("felacoo_token"); setUser(false); } return; }
      if (localStorage.getItem("felacoo_token")) { setLayer("legacy"); if (!(await loadMe())) { localStorage.removeItem("felacoo_token"); setUser(false); } return; }
      setUser(false);
    })();
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "TOKEN_REFRESHED" || event === "SIGNED_IN") { mirrorToken(session); setLayer("supabase"); }
      if (event === "SIGNED_OUT") { localStorage.removeItem("felacoo_token"); setUser(false); }
    });
    return () => { alive = false; sub.subscription.unsubscribe(); };
  }, [loadMe]);

  const finishLogin = async (data, email, password) => {
    localStorage.setItem("felacoo_token", data.token); // legacy token first — the app is usable even if Supabase is unreachable
    setLayer("legacy");
    if (data.supabase) {
      const { data: sb, error } = await supabase.auth.signInWithPassword({ email, password });
      if (!error && sb.session) { mirrorToken(sb.session); setLayer("supabase"); }
    }
    setUser(data.user); setBalance(data.user.balance ?? 10000);
    return data.user;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password }); // verifies bcrypt + lazily migrates the identity
    return finishLogin(data, email, password);
  };
  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    return finishLogin(data, email, password);
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    try { await supabase.auth.signOut(); } catch {}
    localStorage.removeItem("felacoo_token");
    setUser(false);
  };

  return <AuthContext.Provider value={{ user, balance, setBalance, login, register, logout, authLayer: layer }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
